import React, { useEffect, useState } from "react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Nav,
  NavItem,
  Row,
  TabContent,
  TabPane,
  NavLink,
} from "reactstrap";
import { useFormik } from "formik";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import UniversityService from "../../../services/KangarooServices/UniversityService";
import toast from "react-hot-toast";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DestinationService from "../../../services/KangarooServices/DestinationService";
import Select from "react-select";

const InstituteIntellForm = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [intellData, setIntellData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [destinationIntellData, setDestinationIntellData] = useState([]);
  const [institutedata, setInstituteData] = useState([]);
  const [selectedDestination, setSelectedDestination] = useState(null);

  const { id } = useParams();
  const navigate = useNavigate();
  const isUpdate = window.location.pathname.includes("/update/");

  // Fetch existing intelligence for update
  const fetchInstituteIntell = async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await UniversityService.getIntellbyId(id);
      setIntellData(response || []);
    } catch (error) {
      console.log(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch list of destinations / institutes
  const fetchUniversityData = async () => {
    try {
      const response = await DestinationService.getList();
      setInstituteData(response || []);
    } catch (error) {
      console.log(error);
    }
  };

  // Fetch headings for selected destination
  const fetchDestinationIntell = async () => {
    if (!selectedDestination) return;
    try {
      const response = await DestinationService.getDetailIntell(
        selectedDestination,
      );
      setDestinationIntellData(response || []);
    } catch (error) {
      console.log(error.message);
    }
  };

  useEffect(() => {
    fetchUniversityData();
  }, []);

  useEffect(() => {
    fetchInstituteIntell(id);
  }, [id]);

  useEffect(() => {
    if (selectedDestination) fetchDestinationIntell();
  }, [selectedDestination]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      sections:
        // Priority 1: existing intellData (update mode, selectedDestination not changed)
        intellData.length
          ? intellData.map((item, index) => ({
              id: item.id,
              headingName: item.headingName,
              orderIndex: item.orderIndex ?? index,
              description: item.description || "",
              url: item.url || "",
            }))
          : // Priority 2: headings from selected destination
            destinationIntellData.map((heading, index) => ({
              countryIntelHeadingId: heading?.id,
              headingName: heading.headingName,
              orderIndex: heading.orderIndex ?? index,
              description: "",
              url: "",
            })),
    },
    onSubmit: async (values) => {
      try {
        if (isUpdate) {
          await UniversityService.updateIntell(id, values.sections);
        } else {
          await UniversityService.createIntell(id, values.sections);
        }
        toast.success("Institute saved successfully");
        navigate("/institute");
      } catch {
        toast.error("Something went wrong");
      }
    },
  });

  // Effect to update form when destinationIntellData changes
  useEffect(() => {
    if (selectedDestination === null) {
      // User cleared the select → restore original intellData
      if (intellData.length) {
        formik.setValues({
          sections: intellData.map((item, index) => ({
            id: item.id,
            headingName: item.headingName,
            orderIndex: item.orderIndex ?? index,
            description: item.description || "",
            url: item.url || "",
          })),
        });
      } else {
        formik.setValues({ sections: [] });
      }
      setActiveTab(0);
    } else {
      // User selected a destination
      if (destinationIntellData.length) {
        // destination has headings → populate them
        formik.setValues({
          sections: destinationIntellData.map((heading, index) => ({
            countryIntelHeadingId: heading?.id,
            headingName: heading.headingName,
            orderIndex: heading.orderIndex ?? index,
            description: "",
            url: "",
          })),
        });
        setActiveTab(0);
      } else {
        // destination has no headings → clear form
        formik.setValues({ sections: [] });
        setActiveTab(0);
      }
    }
  }, [destinationIntellData, selectedDestination]); // only trigger when these change

  const addSection = () => {
    const updated = [
      ...formik.values.sections,
      { headingName: "", orderIndex: null, description: "", url: "" },
    ];
    formik.setFieldValue("sections", updated);
    setActiveTab(updated.length - 1);
  };

  const removeSection = (index) => {
    const updated = [...formik.values.sections];
    updated.splice(index, 1);
    formik.setFieldValue("sections", updated);
    setActiveTab((prev) => (prev > 0 ? prev - 1 : 0));
  };

  const instituteOptions = institutedata.map((i) => ({
    value: i.id,
    label: i.name,
  }));

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Institute Intelligence"
          pageTitle="Affiliated Institute"
          breadcrumbItems={[{ title: "Back to List", link: "/institute" }]}
        />

        <form onSubmit={formik.handleSubmit}>
          <Row>
            <Col>
              <div className="mb-3">
                <Select
                  options={instituteOptions}
                  value={
                    instituteOptions.find(
                      (opt) => opt.value === selectedDestination,
                    ) || null
                  }
                  isClearable={true}
                  onChange={(selected) =>
                    setSelectedDestination(selected ? selected.value : null)
                  }
                  placeholder="Select destination country"
                />
              </div>
            </Col>
            <Col>
              <div className="d-flex justify-content-end gap-3 mb-3">
                <button type="submit" className="btn btn-success">
                  {isUpdate ? "Update" : "Create"}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => navigate("/institute")}
                >
                  Cancel
                </button>
              </div>
            </Col>
          </Row>

          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Sections</h5>
                {/* <Button color="primary" size="sm" onClick={addSection}>
                  + Add Section
                </Button> */}
              </div>

              {/* Tabs */}
              <Nav tabs className="mb-3">
                {formik.values.sections.map((_, index) => (
                  <NavItem key={index}>
                    <NavLink
                      active={activeTab === index}
                      onClick={() => setActiveTab(index)}
                      style={{ cursor: "pointer" }}
                    >
                      Section {index + 1}
                      {/* {formik.values.sections.length > 1 && (
                        <i
                          className="mdi mdi-close ms-2"
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            removeSection(index);
                          }}
                        />
                      )} */}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              {/* Tab Content */}
              <TabContent activeTab={activeTab}>
                {formik.values.sections.map((section, index) => (
                  <TabPane tabId={index} key={index}>
                    <Row>
                      <Col md={12}>
                        <div className="mb-2">
                          <Label>
                            Heading Name <span className="text-danger">*</span>
                          </Label>
                          <Input
                            value={section.headingName}
                            onChange={(e) =>
                              formik.setFieldValue(
                                `sections.${index}.headingName`,
                                e.target.value,
                              )
                            }
                            readOnly
                          />
                        </div>
                      </Col>

                      {/* <Col md={4}>
                        <div className="mb-2">
                          <Label>
                            Order Index <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="number"
                            value={section.orderIndex}
                            onChange={(e) =>
                              formik.setFieldValue(
                                `sections.${index}.orderIndex`,
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </Col> */}
                      <Col md={8}>
                        <div className="mb-2">
                          <Label>Url</Label>
                          <Input
                            type="text"
                            value={section.url}
                            onChange={(e) =>
                              formik.setFieldValue(
                                `sections.${index}.url`,
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </Col>
                      <Col md={12}>
                        <div className="mb-2">
                          <Label>
                            Description <span className="text-danger">*</span>
                          </Label>
                          <CKEditor
                            editor={ClassicEditor}
                            data={section.description}
                            onChange={(event, editor) =>
                              formik.setFieldValue(
                                `sections.${index}.description`,
                                editor.getData(),
                              )
                            }
                          />
                        </div>
                      </Col>
                    </Row>
                  </TabPane>
                ))}
              </TabContent>
            </CardBody>
          </Card>
        </form>
      </Container>
    </div>
  );
};

export default InstituteIntellForm;
